// Health details never travel in the body of an email.
//
// /messages/send refuses a body that reads as clinical (diagnoses, medications,
// dosages, lab values) with error `phi_requires_attachment` before anything is
// sent, the same rule Ellie's own send_email tool follows. The way through is
// to repeat the send with `deliverAsPdf`: the server puts the typed text in a
// PDF attachment and the email carries a short cover note instead.

import { confirmAsync } from './confirm';

export const PHI_REQUIRES_ATTACHMENT = 'phi_requires_attachment';

interface SendResult {
  success?: boolean;
  message?: string;
  error?: string;
}

export const needsPdfAttachment = (result: SendResult | null | undefined): boolean =>
  result?.error === PHI_REQUIRES_ATTACHMENT;

/**
 * Send, and when the server refuses health details in the body, ask before
 * sending the same message as a PDF attachment.
 *
 * Resolves to null when the person chose to keep editing — callers must treat
 * that as "stop", not as an error.
 */
export async function sendOrOfferPdf(
  send: (deliverAsPdf: boolean) => Promise<SendResult>,
): Promise<SendResult | null> {
  const result = await send(false);
  if (!needsPdfAttachment(result)) return result;
  const confirmed = await confirmAsync({
    title: 'Send as a PDF attachment?',
    message:
      "Your message includes health information, which can't go in the body of an email. " +
      'It will be sent as a PDF attachment with a short cover note.',
    confirmText: 'Send as PDF',
  });
  return confirmed ? send(true) : null;
}
